/**
 * FERP Signing Ceremony Hook (DRR-091)
 *
 * Wraps codegen-generated ceremony GraphQL hooks for the signing
 * ceremony workflow. Polls every 5s while ceremony is active.
 *
 * FERP Principles:
 * - Dumb Frontend: ceremony state from backend, no local state machine
 * - Backend IDs only: ceremony_id generated server-side
 * - Polling over subscriptions (sufficient for ceremony flow)
 * - All types from @/generated/graphql (no local type definitions)
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CeremonyStatus,
  type SigningCeremony,
  type CreateSigningCeremonyInput,
  useGetCeremonyStatusLazyQuery,
  useCreateSigningCeremonyMutation,
  useRecordParticipantSignatureMutation,
  useDeclineCeremonyMutation,
} from '@/generated/graphql';

// Active statuses that warrant polling
const ACTIVE_STATUSES = new Set<string>([
  CeremonyStatus.DRAFT,
  CeremonyStatus.SENT,
  CeremonyStatus.SIGNING,
]);
const POLL_INTERVAL_MS = 5000;

export function useSigningCeremony(ceremonyId?: string, tenantId?: string) {
  const [ceremony, setCeremony] = useState<SigningCeremony | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Lazy query for polling — Apollo v4 removed onCompleted from lazy query options,
  // so we use the exec function's returned promise instead.
  const [fetchStatus] = useGetCeremonyStatusLazyQuery({
    fetchPolicy: 'network-only',
  });

  // Mutations
  const [createMutation, { loading: creating }] = useCreateSigningCeremonyMutation();
  const [signMutation, { loading: signing }] = useRecordParticipantSignatureMutation();
  const [declineMutation, { loading: declining }] = useDeclineCeremonyMutation();

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  // Process query result: update ceremony state, stop polling when terminal
  const handleQueryResult = useCallback(
    (result: { data?: { getCeremonyStatus?: SigningCeremony | null } }) => {
      const status = result.data?.getCeremonyStatus;
      if (status) {
        setCeremony(status as SigningCeremony);
        if (!ACTIVE_STATUSES.has(status.status)) {
          stopPolling();
        }
      }
    },
    [stopPolling],
  );

  const startPolling = useCallback(
    (cId: string, tId: string) => {
      stopPolling();
      pollRef.current = setInterval(() => {
        fetchStatus({ variables: { ceremonyId: cId, tenantId: tId } }).then(
          handleQueryResult,
        );
      }, POLL_INTERVAL_MS);
    },
    [fetchStatus, stopPolling, handleQueryResult],
  );

  // Initial fetch + polling setup when ceremonyId changes
  useEffect(() => {
    if (ceremonyId && tenantId) {
      fetchStatus({ variables: { ceremonyId, tenantId } }).then(
        handleQueryResult,
      );
      startPolling(ceremonyId, tenantId);
    }
    return stopPolling;
  }, [ceremonyId, tenantId, fetchStatus, startPolling, stopPolling, handleQueryResult]);

  // Create a new ceremony
  const createCeremony = useCallback(
    async (input: CreateSigningCeremonyInput) => {
      const { data } = await createMutation({ variables: { input } });
      const created = data?.createSigningCeremony as SigningCeremony | undefined;
      if (created) {
        setCeremony(created);
        if (ACTIVE_STATUSES.has(created.status)) {
          startPolling(created.ceremonyId, created.tenantId);
        }
      }
      return created;
    },
    [createMutation, startPolling],
  );

  // Record a participant's signature (with optional PAdES visual signature image)
  const recordSignature = useCallback(
    async (participantIndex: number, signatureImage?: string) => {
      if (!ceremony) return null;
      const { data } = await signMutation({
        variables: {
          input: {
            ceremonyId: ceremony.ceremonyId,
            tenantId: ceremony.tenantId,
            participantIndex,
            ...(signatureImage ? { signatureImage } : {}),
          },
        },
      });
      const updated = data?.recordParticipantSignature as SigningCeremony | undefined;
      if (updated) setCeremony(updated);
      return updated;
    },
    [ceremony, signMutation],
  );

  // Decline the ceremony
  const decline = useCallback(
    async (participantIndex: number, reason?: string) => {
      if (!ceremony) return null;
      const { data } = await declineMutation({
        variables: {
          input: {
            ceremonyId: ceremony.ceremonyId,
            tenantId: ceremony.tenantId,
            participantIndex,
            reason,
          },
        },
      });
      const updated = data?.declineCeremony as SigningCeremony | undefined;
      if (updated) {
        setCeremony(updated);
        stopPolling();
      }
      return updated;
    },
    [ceremony, declineMutation, stopPolling],
  );

  return {
    ceremony,
    loading: creating || signing || declining,
    creating,
    signing,
    declining,
    createCeremony,
    recordSignature,
    decline,
    refetch: () => {
      if (ceremonyId && tenantId) {
        fetchStatus({ variables: { ceremonyId, tenantId } }).then(
          handleQueryResult,
        );
      }
    },
  };
}
